/** App RBAC — sidebar module codes match app_rbac_modules.code */

export const KNOWN_APP_MODULE_CODES = [
  'jobs',
  'candidates',
  'assessments',
  'messenger',
  'onboarding',
  'insights',
  'settings',
];

export const APP_MENU_MODULE_BY_NAME = {
  Jobs: 'jobs',
  Candidates: 'candidates',
  Assessments: 'assessments',
  Messenger: 'messenger',
  Onboarding: 'onboarding',
  'Insights ': 'insights',
  Insights: 'insights',
  Settings: 'settings',
};

/** Canonical permission codes (must match app_rbac_permissions.code). */
export const P = {
  JOBS_JOB_READ: 'jobs.job.read',
  JOBS_JOB_CREATE: 'jobs.job.create',
  JOBS_JOB_EDIT: 'jobs.job.edit',
  JOBS_JOB_DELETE: 'jobs.job.delete',
  JOBS_ASSIGNMENT_READ: 'jobs.assignment.read',
  JOBS_ASSIGNMENT_CREATE: 'jobs.assignment.create',
  JOBS_ASSIGNMENT_EDIT: 'jobs.assignment.edit',
  JOBS_ASSIGNMENT_DELETE: 'jobs.assignment.delete',
  JOBS_SETTINGS_READ: 'jobs.settings.read',
  JOBS_SETTINGS_EDIT: 'jobs.settings.edit',
  JOBS_AUTOMATION_READ: 'jobs.automation.read',
  JOBS_AUTOMATION_EDIT: 'jobs.automation.edit',
  JOBS_JOB_BOARD_READ: 'jobs.job_board.read',
  JOBS_JOB_BOARD_PUBLISH: 'jobs.job_board.publish',
  CANDIDATES_CANDIDATE_READ: 'candidates.candidate.read',
  CANDIDATES_CANDIDATE_CREATE: 'candidates.candidate.create',
  CANDIDATES_CANDIDATE_EDIT: 'candidates.candidate.edit',
  CANDIDATES_CANDIDATE_DELETE: 'candidates.candidate.delete',
  ASSESSMENTS_ASSESSMENT_READ: 'assessments.assessment.read',
  ASSESSMENTS_ASSESSMENT_CREATE: 'assessments.assessment.create',
  ASSESSMENTS_ASSESSMENT_EDIT: 'assessments.assessment.edit',
  ASSESSMENTS_ASSESSMENT_DELETE: 'assessments.assessment.delete',
  MESSENGER_THREAD_READ: 'messenger.thread.read',
  MESSENGER_THREAD_CREATE: 'messenger.thread.create',
  MESSENGER_THREAD_EDIT: 'messenger.thread.edit',
  MESSENGER_THREAD_DELETE: 'messenger.thread.delete',
  ONBOARDING_FLOW_READ: 'onboarding.flow.read',
  ONBOARDING_FLOW_CREATE: 'onboarding.flow.create',
  ONBOARDING_FLOW_EDIT: 'onboarding.flow.edit',
  ONBOARDING_FLOW_DELETE: 'onboarding.flow.delete',
  INSIGHTS_REPORT_READ: 'insights.report.read',
  SETTINGS_COMPANY_READ: 'settings.company.read',
  SETTINGS_COMPANY_CREATE: 'settings.company.create',
  SETTINGS_COMPANY_EDIT: 'settings.company.edit',
  SETTINGS_COMPANY_DELETE: 'settings.company.delete',
  SETTINGS_CLIENT_COMPANY_READ: 'settings.client_company.read',
  SETTINGS_CLIENT_COMPANY_CREATE: 'settings.client_company.create',
  SETTINGS_CLIENT_COMPANY_EDIT: 'settings.client_company.edit',
  SETTINGS_CLIENT_COMPANY_DELETE: 'settings.client_company.delete',
  SETTINGS_MEMBERS_READ: 'settings.members.read',
  SETTINGS_MEMBERS_CREATE: 'settings.members.create',
  SETTINGS_MEMBERS_EDIT: 'settings.members.edit',
  SETTINGS_MEMBERS_DELETE: 'settings.members.delete',
  SETTINGS_ROLES_READ: 'settings.roles.read',
  SETTINGS_ROLES_CREATE: 'settings.roles.create',
  SETTINGS_ROLES_EDIT: 'settings.roles.edit',
  SETTINGS_ROLES_DELETE: 'settings.roles.delete',
  SETTINGS_BILLING_READ: 'settings.billing.read',
  SETTINGS_BILLING_CREATE: 'settings.billing.create',
  SETTINGS_BILLING_EDIT: 'settings.billing.edit',
  SETTINGS_BILLING_DELETE: 'settings.billing.delete',
  SETTINGS_INTEGRATION_READ: 'settings.integration.read',
  SETTINGS_INTEGRATION_CREATE: 'settings.integration.create',
  SETTINGS_INTEGRATION_EDIT: 'settings.integration.edit',
  SETTINGS_INTEGRATION_DELETE: 'settings.integration.delete',
};

/**
 * Filter layout menuList by module_codes from /auth/me company_user.
 * Super-admin (roleId === "1") bypasses this filter at the call site.
 */
export function filterMenuByModuleCodes(menuList = [], moduleCodes = null) {
  if (!Array.isArray(menuList)) return [];
  // null/undefined = no RBAC context yet → show full menu (legacy)
  if (moduleCodes == null) return menuList;
  const allowed = new Set((moduleCodes || []).map(String));
  return menuList.filter((item) => {
    const moduleCode = APP_MENU_MODULE_BY_NAME[item.name];
    if (!moduleCode) return true;
    return allowed.has(moduleCode);
  });
}

export function getPrimaryCompanyUser(meDetails) {
  const companies = meDetails?.companies || meDetails?.data?.companies || [];
  if (!companies.length) return null;
  return companies[0]?.company_user || null;
}

/** Only gate the menu when /auth/me says this membership uses new app RBAC. */
export function shouldApplyAppRbacMenu(companyUser) {
  if (!companyUser) return false;
  if (companyUser.rbac_source === 'app') return true;
  if (companyUser.rbac_source === 'legacy') return false;
  // Fallback: known module codes from new catalog
  const codes = companyUser.module_codes || [];
  return codes.some((c) => KNOWN_APP_MODULE_CODES.includes(String(c)));
}

/**
 * Modules the member may open from the sidebar: those with at least one `<module>.*.read`
 * permission. Create/edit alone (e.g. inviting candidates to a job) doesn't grant the page.
 */
export function getReadableModuleCodes(companyUser) {
  const modules = new Set();
  (companyUser?.permission_codes || []).forEach((code) => {
    const [moduleCode, , action] = String(code).split('.');
    if (action === 'read') modules.add(moduleCode);
  });
  return [...modules];
}

/**
 * Sidebar rules for the signed-in member. Members with app RBAC roles are filtered by
 * the modules they can read; everyone else falls back to the account role name:
 * HIRING_MANAGER loses Settings, RECRUITER only gets Jobs and Messenger.
 * Backend may send "Manager", "Hiring Manager" or "HIRING_MANAGER", so spaces become underscores.
 */
export function getMemberMenuRules({ companyUser = null, isSuperAdmin = false, roleName = '' } = {}) {
  const useAppRbacMenu = !isSuperAdmin && shouldApplyAppRbacMenu(companyUser);
  const accountRole = String(roleName || '').trim().toUpperCase().replace(/\s+/g, '_');
  return {
    moduleCodes: useAppRbacMenu ? getReadableModuleCodes(companyUser) : null,
    isRecruiter: !useAppRbacMenu && accountRole === 'RECRUITER',
    isHiringManager: !useAppRbacMenu && (accountRole === 'HIRING_MANAGER' || accountRole === 'MANAGER'),
  };
}

export function filterMenuForMember(menuList = [], { moduleCodes = null, isRecruiter = false, isHiringManager = false } = {}) {
  return filterMenuByModuleCodes(menuList, moduleCodes).filter((item) => {
    if (isRecruiter) return ['Jobs', 'Messenger'].includes(item.name);
    if (isHiringManager) return item.name !== 'Settings';
    return true;
  });
}

/**
 * Check a single app permission code.
 * Returns true for legacy users (no app RBAC) so existing tenants keep working.
 */
export function hasAppPermission(meDetails, permissionCode) {
  if (!permissionCode) return true;
  const cu = getPrimaryCompanyUser(meDetails);
  if (!cu) return true; // no company context yet / legacy
  if (!shouldApplyAppRbacMenu(cu)) return true;
  const codes = cu.permission_codes || [];
  return codes.includes(permissionCode);
}

/** True if the user has any of the given permission codes. */
export function hasAnyAppPermission(meDetails, permissionCodes = []) {
  if (!permissionCodes.length) return true;
  return permissionCodes.some((code) => hasAppPermission(meDetails, code));
}

/** True if the user has all of the given permission codes. */
export function hasAllAppPermissions(meDetails, permissionCodes = []) {
  if (!permissionCodes.length) return true;
  return permissionCodes.every((code) => hasAppPermission(meDetails, code));
}

// Convenience wrappers (kept for existing call sites)
export function canCreateJob(meDetails) {
  return hasAppPermission(meDetails, P.JOBS_JOB_CREATE);
}

export function canEditJob(meDetails) {
  return hasAppPermission(meDetails, P.JOBS_JOB_EDIT);
}

export function canDeleteJob(meDetails) {
  return hasAppPermission(meDetails, P.JOBS_JOB_DELETE);
}

/** Invite candidates into a job (job header + /invite page). */
export function canInviteCandidatesToJob(meDetails) {
  return hasAppPermission(meDetails, P.CANDIDATES_CANDIDATE_CREATE);
}

/**
 * Job Settings tab visibility (read or edit). Soft-fallback to jobs.job.edit.
 */
export function canViewJobSettings(meDetails) {
  return hasAnyAppPermission(meDetails, [
    P.JOBS_SETTINGS_READ,
    P.JOBS_SETTINGS_EDIT,
    P.JOBS_JOB_EDIT,
  ]);
}

/**
 * Job Settings write access. Soft-fallback to jobs.job.edit.
 */
export function canManageJobSettings(meDetails) {
  return hasAnyAppPermission(meDetails, [P.JOBS_SETTINGS_EDIT, P.JOBS_JOB_EDIT]);
}

/**
 * Automation tab visibility (read or edit). Soft-fallback to jobs.job.edit.
 */
export function canViewJobAutomation(meDetails) {
  return hasAnyAppPermission(meDetails, [
    P.JOBS_AUTOMATION_READ,
    P.JOBS_AUTOMATION_EDIT,
    P.JOBS_JOB_EDIT,
  ]);
}

/**
 * Automation write access. Soft-fallback to jobs.job.edit.
 */
export function canManageJobAutomation(meDetails) {
  return hasAnyAppPermission(meDetails, [P.JOBS_AUTOMATION_EDIT, P.JOBS_JOB_EDIT]);
}

/**
 * Account (login) roles from the assessment API, assignable via user/add-user.
 * Separate from app RBAC roles above. The API rejects a non-default role unless
 * the caller is SuperAdmin (1) or Admin (2).
 */
export const DEFAULT_ACCOUNT_ROLE_ID = 3;

export const ACCOUNT_ROLE_OPTIONS = [
  { id: 3, label: 'Recruiter' },
  { id: 7, label: 'Manager' },
  { id: 4, label: 'User' },
];

export function canAssignAccountRole(profileDetails) {
  return ['1', '2'].includes(String(profileDetails?.roleId ?? ''));
}
